import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useNavigate, useParams } from 'react-router-dom';
import { FirebaseService } from '../services/firebaseService';
import Footer from './Footer';

interface EclairagePublicEnsembleDetailProps {
  t: any;
  isRTL: boolean;
}

const DetailPage = styled.div<{ $isRTL: boolean }>`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding-top: 120px;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const Breadcrumbs = styled(motion.div)<{ $isRTL: boolean }>`
  padding: 1rem 0;
  font-size: 0.9rem;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const BreadcrumbLink = styled.span<{ $isLast: boolean }>`
  color: ${props => props.$isLast ? '#dc2626' : '#64748b'};
  text-decoration: none;
  font-weight: ${props => props.$isLast ? '600' : '400'};
  cursor: ${props => props.$isLast ? 'default' : 'pointer'};
  transition: color 0.3s ease;
  
  &:hover {
    color: ${props => props.$isLast ? '#dc2626' : '#dc2626'};
  }
`;

const HeroSection = styled.div`
  background: #f8f9fa;
  border-radius: 24px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-bottom: 4rem;
  position: relative;
`;

const HeroBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(220, 38, 38, 0.05) 0%, rgba(239, 68, 68, 0.02) 100%);
  z-index: 0;
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  padding: 3rem;
`;

const PageLayout = styled.div`
  display: flex;
  gap: 4rem;
  align-items: flex-start;
  
  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 3rem;
  }
`;

const ImageSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const MainImage = styled.div`
  width: 100%;
  height: 500px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 20px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(220, 38, 38, 0.05) 0%, rgba(239, 68, 68, 0.05) 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover::after {
    opacity: 1;
  }
  
  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    transition: transform 0.3s ease;
    filter: drop-shadow(0 8px 16px rgba(0, 0, 0, 0.1));
  }
  
  &:hover img {
    transform: scale(1.02);
  }
`;

const ThumbnailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  gap: 1rem;
`;

const Thumbnail = styled.div<{ $isActive: boolean }>`
  height: 100px;
  background: #f8f9fa;
  border-radius: 12px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: 3px solid ${props => props.$isActive ? '#dc2626' : 'transparent'};
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &:hover {
    border-color: #dc2626;
    transform: translateY(-2px);
    box-shadow: 0 6px 15px rgba(0, 0, 0, 0.15);
  }
  
  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    transition: transform 0.3s ease;
  }
  
  &:hover img {
    transform: scale(1.05);
  }
`;

const InfoSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
`;

const EnsembleTitle = styled.h1<{ $isRTL: boolean }>`
  font-size: 3rem;
  font-weight: 800;
  color: #1f2937;
  margin: 0;
  line-height: 1.2;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  background: linear-gradient(135deg, #1f2937 0%, #374151 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const EnsembleDescription = styled.div<{ $isRTL: boolean }>`
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 20px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  padding: 2.5rem;
  border: 1px solid rgba(220, 38, 38, 0.1);
  
  p {
    color: #374151;
    line-height: 1.7;
    margin: 0;
    font-size: 1.1rem;
    font-family: ${props => props.$isRTL ? 
      "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
      "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  }
`;

const ProductsSection = styled.div<{ $isRTL: boolean }>`
  background: #f8f9fa;
  border-radius: 20px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
  padding: 2.5rem;
  border: 1px solid rgba(220, 38, 38, 0.1);
`;

const SectionTitle = styled.h3<{ $isRTL: boolean }>`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 2rem 0;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 60px;
    height: 3px;
    background: linear-gradient(90deg, #dc2626, #ef4444);
    border-radius: 2px;
  }
`;

const ProductsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
`;

const ProductCard = styled.div<{ $isRTL: boolean }>`
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 1px solid rgba(220, 38, 38, 0.1);
  border-radius: 16px;
  padding: 1.5rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  font-weight: 600;
  color: #1f2937;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #dc2626, #ef4444);
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
    border-color: rgba(220, 38, 38, 0.3);
    
    &::before {
      transform: scaleX(1);
    }
  }
`;

const CompositionSection = styled(motion.div)`
  margin-top: 4rem;
  padding-bottom: 4rem;
`;

const CompositionTitle = styled.h2<{ $isRTL: boolean }>`
  font-size: 2.5rem;
  font-weight: 800;
  color: #1f2937;
  margin: 0 0 3rem 0;
  text-align: center;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  background: linear-gradient(135deg, #1f2937 0%, #374151 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const CompositionLayout = styled.div`
  display: flex;
  gap: 4rem;
  align-items: flex-start;
  
  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 3rem;
  }
`;

const CompositionImage = styled.div`
  flex: 1;
  background: #f8f9fa;
  border-radius: 20px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  padding: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(220, 38, 38, 0.1);
  
  img {
    max-width: 100%;
    max-height: 500px;
    object-fit: contain;
    filter: drop-shadow(0 8px 16px rgba(0, 0, 0, 0.1));
  }
`;

const CompositionInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
`;

const PDFButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const PDFButton = styled.a`
  background: linear-gradient(135deg, #dc2626 0%, #ef4444 100%);
  color: white;
  text-decoration: none;
  padding: 1.5rem 2rem;
  border-radius: 16px;
  font-weight: 700;
  text-align: center;
  transition: all 0.3s ease;
  font-size: 1.1rem;
  box-shadow: 0 8px 20px rgba(220, 38, 38, 0.3);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s ease;
  }
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 30px rgba(220, 38, 38, 0.4);
    
    &::before {
      left: 100%;
    }
  }
  
  &:active {
    transform: translateY(-2px);
  }
`;

const TechnicalTable = styled.div`
  background: #f8f9fa;
  border-radius: 20px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  border: 1px solid rgba(220, 38, 38, 0.1);
`;

const TableHeader = styled.div`
  background: linear-gradient(135deg, #dc2626 0%, #ef4444 100%);
  color: white;
  padding: 2rem;
  font-size: 1.3rem;
  font-weight: 700;
  text-align: center;
`;

const TableRow = styled.div<{ $isRTL: boolean }>`
  display: grid;
  grid-template-columns: 1fr 1fr;
  border-bottom: 1px solid #e5e7eb;
  transition: background-color 0.3s ease;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background-color: #f8fafc;
  }
`;

const TableCell = styled.div<{ $isRTL: boolean; $isHeader?: boolean }>`
  padding: 1.5rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  font-weight: ${props => props.$isHeader ? '700' : '500'};
  color: ${props => props.$isHeader ? '#374151' : '#1f2937'};
  background: ${props => props.$isHeader ? '#f8fafc' : 'white'};
  border-right: 1px solid #e5e7eb;
  font-size: 1rem;
  
  &:last-child {
    border-right: none;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
  font-size: 1.2rem;
  color: #64748b;
  background: #f8f9fa;
  border-radius: 20px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
`;

const ErrorState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  background: #f8f9fa;
  border-radius: 20px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
`;

const ErrorIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
`;

const ErrorTitle = styled.h3<{ $isRTL: boolean }>`
  font-size: 1.5rem;
  font-weight: 700;
  color: #dc2626;
  margin-bottom: 1rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const ErrorText = styled.p<{ $isRTL: boolean }>`
  color: #64748b;
  font-size: 1.1rem;
  line-height: 1.6;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

interface Ensemble {
  id: string;
  title: string;
  description: string;
  mainImage: string;
  additionalImages: string[];
  products: string[];
  compositionImage: string;
  brochurePDF: string;
  catalogPDF: string;
  technicalSpecs: { [key: string]: string };
  subcategoryId: string;
  createdAt: Date;
  updatedAt: Date;
}

const EclairagePublicEnsembleDetail: React.FC<EclairagePublicEnsembleDetailProps> = ({ t, isRTL }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  const navigate = useNavigate();
  const { ensembleId } = useParams<{ ensembleId: string }>();
  const [ensemble, setEnsemble] = useState<Ensemble | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (ensembleId) {
      loadEnsemble(ensembleId);
    }
  }, [ensembleId]);

  const loadEnsemble = async (id: string) => {
    try {
      setLoading(true);
      const ensembleData = await FirebaseService.getById('eclairage-public-ensembles', id) as Ensemble;
      setEnsemble(ensembleData);
    } catch (error) {
      console.error('Error loading ensemble:', error);
    } finally {
      setLoading(false);
    }
  };

  const breadcrumbs = [
    { label: "Accueil", path: "/" },
    { label: "Catalogue produits", path: "/catalogue" },
    { label: "Éclairage Public", path: "/category/eclairage-public" },
    { label: ensemble?.title || "Ensemble", path: `/category/eclairage-public/${ensembleId}` },
  ];

  if (loading) {
    return (
      <DetailPage $isRTL={isRTL}>
        <Container>
          <LoadingSpinner>Chargement des détails de l'ensemble...</LoadingSpinner>
        </Container>
      </DetailPage>
    );
  }

  if (!ensemble) {
    return (
      <DetailPage $isRTL={isRTL}>
        <Container>
          <ErrorState>
            <ErrorIcon>⚠️</ErrorIcon>
            <ErrorTitle $isRTL={isRTL}>Ensemble non trouvé</ErrorTitle>
            <ErrorText $isRTL={isRTL}>
              L'ensemble que vous recherchez n'existe pas ou a été supprimé.
            </ErrorText>
          </ErrorState>
        </Container>
      </DetailPage>
    );
  }

  // Only use additional images, exclude primary image
  const images = ensemble.additionalImages || [];

  return (
    <DetailPage $isRTL={isRTL}>
      <Container>
        <Breadcrumbs $isRTL={isRTL}>
          {breadcrumbs.map((breadcrumb, index) => (
            <React.Fragment key={index}>
              <BreadcrumbLink
                $isLast={index === breadcrumbs.length - 1}
                onClick={() => index < breadcrumbs.length - 1 && navigate(breadcrumb.path)}
              >
                {breadcrumb.label}
              </BreadcrumbLink>
              {index < breadcrumbs.length - 1 && <span>›</span>}
            </React.Fragment>
          ))}
        </Breadcrumbs>

        <HeroSection>
          <HeroBackground />
          <HeroContent>
            <PageLayout>
              {images.length > 0 && (
                <ImageSection>
                  <MainImage>
                    <img src={images[selectedImage]} alt={ensemble.title} />
                  </MainImage>
                  {images.length > 1 && (
                    <ThumbnailGrid>
                      {images.map((image, index) => (
                        <Thumbnail
                          key={index}
                          $isActive={selectedImage === index}
                          onClick={() => setSelectedImage(index)}
                        >
                          <img src={image} alt={`${ensemble.title} ${index + 1}`} />
                        </Thumbnail>
                      ))}
                    </ThumbnailGrid>
                  )}
                </ImageSection>
              )}

              <InfoSection>
                <EnsembleTitle $isRTL={isRTL}>{ensemble.title}</EnsembleTitle>
                
                <EnsembleDescription $isRTL={isRTL}>
                  <p>{ensemble.description}</p>
                </EnsembleDescription>

                <ProductsSection $isRTL={isRTL}>
                  <SectionTitle $isRTL={isRTL}>Produits disponibles</SectionTitle>
                  <ProductsList>
                    {ensemble.products.map((product, index) => (
                      <ProductCard key={index} $isRTL={isRTL}>
                        {product}
                      </ProductCard>
                    ))}
                  </ProductsList>
                </ProductsSection>
              </InfoSection>
            </PageLayout>
          </HeroContent>
        </HeroSection>

        <CompositionSection ref={ref}>
          <CompositionTitle $isRTL={isRTL}>Composition de l'ensemble</CompositionTitle>
          <CompositionLayout>
            <CompositionImage>
              <img src={ensemble.compositionImage} alt={`Composition ${ensemble.title}`} />
            </CompositionImage>
            <CompositionInfo>
              {/* Only show PDF buttons if at least one PDF is available */}
              {(ensemble.brochurePDF || ensemble.catalogPDF) && (
                <PDFButtons>
                  {ensemble.brochurePDF && (
                    <PDFButton href={ensemble.brochurePDF} target="_blank" rel="noopener noreferrer">
                      ⬇️ Télécharger la Brochure
                    </PDFButton>
                  )}
                  {ensemble.catalogPDF && (
                    <PDFButton href={ensemble.catalogPDF} target="_blank" rel="noopener noreferrer">
                      ⬇️ Télécharger le Catalogue
                    </PDFButton>
                  )}
                </PDFButtons>
              )}
              
              <TechnicalTable>
                <TableHeader>Caractéristiques techniques</TableHeader>
                {(() => {
                  const orderedSpecs = [
                    { key: 'Modèle', value: ensemble.technicalSpecs['Modèle'] || ensemble.technicalSpecs['Modele'] || ensemble.technicalSpecs.Modele || ensemble.technicalSpecs.modele || '' },
                    { key: 'Forme', value: ensemble.technicalSpecs['Forme'] || ensemble.technicalSpecs.Forme || ensemble.technicalSpecs.forme || '' },
                    { key: 'Matériaux', value: ensemble.technicalSpecs['Matériaux'] || ensemble.technicalSpecs['Materiaux'] || ensemble.technicalSpecs.Materiaux || ensemble.technicalSpecs.materiaux || '' },
                    { key: 'Fixation de luminaire', value: ensemble.technicalSpecs['Fixation de luminaire'] || ensemble.technicalSpecs['fixation de luminaire'] || '' },
                    { key: 'Hauteur du mât', value: ensemble.technicalSpecs['Hauteur du mât'] || ensemble.technicalSpecs['Hauteur du mat'] || ensemble.technicalSpecs['hauteur du mat'] || '' },
                    { key: 'Épaisseur', value: ensemble.technicalSpecs['Épaisseur'] || ensemble.technicalSpecs['Epaisseur'] || ensemble.technicalSpecs.Epaisseur || ensemble.technicalSpecs.epaisseur || '' },
                    { key: 'Certification', value: ensemble.technicalSpecs['Certification'] || ensemble.technicalSpecs.Certification || ensemble.technicalSpecs.certification || '' }
                  ];
                  
                  return orderedSpecs.map(({ key, value }, index) => (
                    <TableRow key={index} $isRTL={isRTL}>
                      <TableCell $isRTL={isRTL} $isHeader={true}>{key}</TableCell>
                      <TableCell $isRTL={isRTL}>{value}</TableCell>
                    </TableRow>
                  ));
                })()}
              </TechnicalTable>
            </CompositionInfo>
          </CompositionLayout>
        </CompositionSection>
      </Container>
      <Footer t={t} isRTL={isRTL} />
    </DetailPage>
  );
};

export default EclairagePublicEnsembleDetail;
